import { z } from 'zod';
import { Photo } from './photo.js';
// Tag definition: an encrypted name attached to a root album. tagId is a
// UUID generated client-side (so the client can wire up associations
// before any round-trip). The name is encrypted with K_album of the root,
// same as encryptedMeta on photos. keyGen mirrors the root's authoritative
// key generation; readers default to 1 for pre-rotation rows.
export const TagDefinition = z.object({
    id: z.string(),
    rootAlbumId: z.string(),
    ownerId: z.string(),
    encryptedName: z.string(),
    keyGen: z.number().int().positive().optional(),
    encryptedNameNext: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
});
// Many-to-many membership of a photo in a tag. Pure structural row — the
// tag name lives only on TagDefinition so this row never participates in
// rotation. albumId carries the photo's leaf album so cascade-delete on
// photo can find these rows by PK = PHOTO#{photoId}.
export const PhotoTag = z.object({
    tagId: z.string(),
    photoId: z.string(),
    albumId: z.string(),
    rootAlbumId: z.string(),
    ownerId: z.string(),
    createdAt: z.string(),
});
// POST /tags — create a tag under a root.
export const CreateTagRequest = z.object({
    rootAlbumId: z.string().min(1),
    tagId: z.string().min(1),
    encryptedName: z.string().min(1),
});
export const CreateTagResponse = TagDefinition;
// PATCH /tags/{tagId} — rename. rootAlbumId is required because the row is
// partitioned by ROOT#{rootAlbumId}, not by tagId alone.
export const RenameTagRequest = z.object({
    rootAlbumId: z.string().min(1),
    encryptedName: z.string().min(1),
});
export const RenameTagResponse = TagDefinition;
export const TagsListResponse = z.object({
    rootAlbumId: z.string(),
    tags: z.array(TagDefinition),
});
// POST /photos/tags/attach-batch — attach N (photo,tag) pairs in a single
// round-trip. All items must share the same rootAlbumId; the handler
// validates each photo belongs to that root and that each tag exists.
export const AttachTagsBatchRequest = z.object({
    rootAlbumId: z.string().min(1),
    items: z
        .array(z.object({
        tagId: z.string().min(1),
        photoId: z.string().min(1),
        albumId: z.string().min(1),
    }))
        .min(1)
        .max(200),
});
export const AttachTagsBatchResponse = z.object({
    attached: z.number().int().nonnegative(),
});
// POST /photos/tags/detach-batch — symmetric.
export const DetachTagsBatchRequest = z.object({
    rootAlbumId: z.string().min(1),
    items: z
        .array(z.object({
        tagId: z.string().min(1),
        photoId: z.string().min(1),
    }))
        .min(1)
        .max(200),
});
export const DetachTagsBatchResponse = z.object({
    detached: z.number().int().nonnegative(),
});
// GET /tags/{tagId}/photos?rootId=…&cursor=… — paginated gallery for a tag.
// Photos are returned as the same Photo DTO used everywhere else so the
// client can decrypt + render with the existing pipeline. cursor is a
// base64-encoded LastEvaluatedKey from DynamoDB (opaque to the client).
export const TagPhotosResponse = z.object({
    tagId: z.string(),
    rootAlbumId: z.string(),
    photos: z.array(Photo),
    nextCursor: z.string().optional(),
});
// GET /photos/{photoId}/tags?albumId=…&rootId=… — list tags attached to a
// single photo (used by the per-photo tag picker). Returns the structural
// PhotoTag rows; the client joins with the cached tag definitions for names.
export const PhotoTagsResponse = z.object({
    photoId: z.string(),
    tags: z.array(PhotoTag),
});
// POST /albums/{id}/rotation/tags/commit — staging step of a key rotation.
// The client re-encrypts every TagDefinition.encryptedName under K_album_new
// and POSTs them here just before /rotation/commit. The backend stores each
// new ciphertext as encryptedNameNext (mirrors Album.encryptedNameNext);
// rotation/commit then swaps encryptedName ← encryptedNameNext atomically
// alongside the album/share swaps. Empty `tags` is valid (root with no tags).
export const RotateTagsCommitRequest = z.object({
    tags: z
        .array(z.object({
        tagId: z.string().min(1),
        encryptedName: z.string().min(1),
    }))
        .max(1000),
});
export const RotateTagsCommitResponse = z.object({
    rootAlbumId: z.string(),
    staged: z.number().int().nonnegative(),
});
//# sourceMappingURL=tag.js.map